import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Popover
} from '@mui/material';
import { format, parseISO, subDays, addDays, startOfWeek, endOfWeek, eachDayOfInterval, subWeeks, addWeeks } from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthContext } from '../../contexts/AuthContext';
import { saveWellnessLog, getWellnessLogs } from '../../utils/firebase';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AppleIcon from '@mui/icons-material/Apple';
import BedIcon from '@mui/icons-material/Bed';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';

const MOOD_CONFIG = {
  great: { icon: SentimentVerySatisfiedIcon, color: '#4caf50', label: 'Great' },
  good: { icon: SentimentSatisfiedIcon, color: '#8bc34a', label: 'Good' },
  okay: { icon: SentimentNeutralIcon, color: '#ffc107', label: 'Okay' },
  bad: { icon: SentimentDissatisfiedIcon, color: '#ff9800', label: 'Bad' },
  terrible: { icon: SentimentVeryDissatisfiedIcon, color: '#f44336', label: 'Terrible' }
};

const WellnessLog = () => {
  const { user } = useContext(AuthContext);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [editData, setEditData] = useState({});
  const [moodAnchorEl, setMoodAnchorEl] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [weekPickerAnchorEl, setWeekPickerAnchorEl] = useState(null);

  // Load logs from Firebase
  useEffect(() => {
    if (!user) return;

    const unsubscribe = getWellnessLogs(user.uid, (logs) => {
      setDailyLogs(logs);
      localStorage.setItem('dailyWellnessLogs', JSON.stringify(logs));
    });

    // Load from localStorage as fallback
    const savedLogs = localStorage.getItem('dailyWellnessLogs');
    if (savedLogs) {
      setDailyLogs(JSON.parse(savedLogs));
    }

    return () => unsubscribe();
  }, [user]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const todayLog = dailyLogs.find(log => log.date === today) || {};

  // Calculate stats for current view
  const stats = useMemo(() => {
    const last30Days = [];
    for (let i = 0; i < 30; i++) {
      const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
      const log = dailyLogs.find(l => l.date === date);
      last30Days.push(log);
    }

    const calcRate = (logs, key) => {
      const filled = logs.filter(l => l);
      if (filled.length === 0) return 0;
      if (key === 'mood') {
        return filled.filter(l => l.mood).length / filled.length * 100;
      }
      return filled.filter(l => l[key]).length / filled.length * 100;
    };

    // Calculate streak
    let streakCount = 0;
    for (let i = 0; i < 365; i++) {
      const streakDate = format(subDays(new Date(), i), 'yyyy-MM-dd');
      const streakLog = dailyLogs.find(l => l.date === streakDate);
      if (streakLog && (streakLog.highlight || streakLog.exercised || streakLog.eatenFruits || streakLog.sleptWell || streakLog.mood)) {
        streakCount++;
      } else {
        break;
      }
    }

    return {
      monthExercise: calcRate(last30Days, 'exercised'),
      monthHealthy: calcRate(last30Days, 'eatenFruits'),
      monthSleep: calcRate(last30Days, 'sleptWell'),
      streak: streakCount
    };
  }, [dailyLogs]);

  // Get days for current week view
  const weekDays = useMemo(() => {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: currentWeekStart, end: weekEnd });

    return days.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const log = dailyLogs.find(l => l.date === dateStr);
      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);

      return {
        date: dateStr,
        dayName: format(date, 'EEE'),
        dayNum: format(date, 'd'),
        monthName: format(date, 'MMM'),
        isToday: dateStr === today,
        isFuture: date > todayDate,
        log: log || null
      };
    });
  }, [currentWeekStart, dailyLogs, today]);

  // Navigation
  const goToPrevWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  const goToNextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const isCurrentWeek = format(currentWeekStart, 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const weekLabel = format(currentWeekStart, 'MMM d') + ' - ' + format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMM d, yyyy');

  const handleSaveLog = async (date, data) => {
    const logData = {
      date,
      ...data,
      timestamp: new Date().toISOString()
    };

    // Update local state
    const updatedLogs = dailyLogs.filter(l => l.date !== date);
    updatedLogs.unshift(logData);
    updatedLogs.sort((a, b) => b.date.localeCompare(a.date));
    setDailyLogs(updatedLogs);
    localStorage.setItem('dailyWellnessLogs', JSON.stringify(updatedLogs));

    // Save to Firebase
    if (user) {
      await saveWellnessLog(user.uid, date, logData);
    }
  };

  const handleToggleToday = async (key) => {
    const newValue = !todayLog[key];
    await handleSaveLog(today, { ...todayLog, [key]: newValue });
  };

  const handleSetMood = async (mood) => {
    await handleSaveLog(today, { ...todayLog, mood });
    setMoodAnchorEl(null);
  };

  const handleHighlightChange = async (value) => {
    await handleSaveLog(today, { ...todayLog, highlight: value });
  };

  const openEditDialog = (date) => {
    const log = dailyLogs.find(l => l.date === date) || {};
    setEditingDate(date);
    setEditData({
      highlight: log.highlight || '',
      exercised: log.exercised || false,
      eatenFruits: log.eatenFruits || false,
      sleptWell: log.sleptWell || false,
      mood: log.mood || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    await handleSaveLog(editingDate, editData);
    setEditDialogOpen(false);
    setEditingDate(null);
  };

  const getMoodIcon = (mood, size = 24) => {
    if (!mood || !MOOD_CONFIG[mood]) return null;
    const config = MOOD_CONFIG[mood];
    const Icon = config.icon;
    return <Icon sx={{ fontSize: size, color: config.color }} />;
  };

  const getCompletionColor = (log) => {
    if (!log) return 'rgba(255, 255, 255, 0.05)';
    const count = [log.exercised, log.eatenFruits, log.sleptWell, log.mood].filter(Boolean).length;
    if (count === 0) return 'rgba(255, 255, 255, 0.05)';
    if (count === 1) return 'rgba(76, 175, 80, 0.2)';
    if (count === 2) return 'rgba(76, 175, 80, 0.35)';
    if (count === 3) return 'rgba(76, 175, 80, 0.5)';
    return 'rgba(76, 175, 80, 0.7)';
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 300 }}>
          Wellness Log
        </Typography>
        {stats.streak > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ color: '#ffc107', fontWeight: 600 }}>
              {stats.streak}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              day streak
            </Typography>
          </Box>
        )}
      </Box>

      {/* Today's Entry */}
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: 3,
          p: 3,
          mb: 3
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: 1 }}>
          Today
        </Typography>

        {/* Highlight input */}
        <TextField
          fullWidth
          value={todayLog.highlight || ''}
          onChange={(e) => handleHighlightChange(e.target.value)}
          placeholder="What made today special?"
          variant="standard"
          sx={{
            mt: 1,
            mb: 3,
            '& .MuiInputBase-input': {
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.9)',
              '&::placeholder': { color: 'rgba(255, 255, 255, 0.3)' }
            },
            '& .MuiInput-underline:before': { borderBottom: '1px solid rgba(255, 255, 255, 0.1)' },
            '& .MuiInput-underline:after': { borderBottom: '2px solid #00897b' }
          }}
        />

        {/* Wellness toggles */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2 }}>
          <Tooltip title="How are you feeling?" arrow>
            <IconButton
              onClick={(e) => setMoodAnchorEl(e.currentTarget)}
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: todayLog.mood ? MOOD_CONFIG[todayLog.mood]?.color : 'rgba(255, 255, 255, 0.1)',
                backgroundColor: todayLog.mood ? `${MOOD_CONFIG[todayLog.mood]?.color}20` : 'transparent',
                '&:hover': { transform: 'scale(1.1)' },
                transition: 'all 0.3s'
              }}
            >
              {todayLog.mood ? getMoodIcon(todayLog.mood, 28) : <SentimentNeutralIcon sx={{ fontSize: 28, color: 'rgba(255, 255, 255, 0.3)' }} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Exercise" arrow>
            <IconButton
              onClick={() => handleToggleToday('exercised')}
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: todayLog.exercised ? '#2196f3' : 'rgba(255, 255, 255, 0.1)',
                backgroundColor: todayLog.exercised ? 'rgba(33, 150, 243, 0.2)' : 'transparent',
                '&:hover': { transform: 'scale(1.1)' },
                transition: 'all 0.3s'
              }}
            >
              <FitnessCenterIcon sx={{ fontSize: 28, color: todayLog.exercised ? '#2196f3' : 'rgba(255, 255, 255, 0.3)' }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Healthy Eating" arrow>
            <IconButton
              onClick={() => handleToggleToday('eatenFruits')}
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: todayLog.eatenFruits ? '#4caf50' : 'rgba(255, 255, 255, 0.1)',
                backgroundColor: todayLog.eatenFruits ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
                '&:hover': { transform: 'scale(1.1)' },
                transition: 'all 0.3s'
              }}
            >
              <AppleIcon sx={{ fontSize: 28, color: todayLog.eatenFruits ? '#4caf50' : 'rgba(255, 255, 255, 0.3)' }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Good Sleep" arrow>
            <IconButton
              onClick={() => handleToggleToday('sleptWell')}
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: todayLog.sleptWell ? '#673ab7' : 'rgba(255, 255, 255, 0.1)',
                backgroundColor: todayLog.sleptWell ? 'rgba(103, 58, 183, 0.2)' : 'transparent',
                '&:hover': { transform: 'scale(1.1)' },
                transition: 'all 0.3s'
              }}
            >
              <BedIcon sx={{ fontSize: 28, color: todayLog.sleptWell ? '#673ab7' : 'rgba(255, 255, 255, 0.3)' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* 30-Day Stats */}
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          p: 2.5,
          mb: 3
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 2 }}>
          Last 30 Days
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          {[
            { label: 'Exercise', value: stats.monthExercise, color: '#2196f3', icon: FitnessCenterIcon },
            { label: 'Eating', value: stats.monthHealthy, color: '#4caf50', icon: AppleIcon },
            { label: 'Sleep', value: stats.monthSleep, color: '#673ab7', icon: BedIcon }
          ].map((stat) => {
            const radius = 22;
            const circumference = 2 * Math.PI * radius;
            const strokeDashoffset = circumference - (stat.value / 100) * circumference;

            return (
              <Box key={stat.label} sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    position: 'relative',
                    mb: 1,
                    mx: 'auto'
                  }}
                >
                  <svg width="54" height="54" style={{ transform: 'rotate(-90deg)' }}>
                    <circle
                      cx="27"
                      cy="27"
                      r={radius}
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="4"
                    />
                    <circle
                      cx="27"
                      cy="27"
                      r={radius}
                      fill="none"
                      stroke={stat.color}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                    />
                  </svg>
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <stat.icon sx={{ fontSize: 18, color: stat.color }} />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                  {Math.round(stat.value)}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  {stat.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Week Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={goToPrevWeek} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            onClick={(e) => setWeekPickerAnchorEl(e.currentTarget)}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              cursor: 'pointer',
              '&:hover': {
                color: 'rgba(255, 255, 255, 1)',
                textDecoration: 'underline'
              }
            }}
          >
            {weekLabel}
          </Typography>
          {!isCurrentWeek && (
            <IconButton size="small" onClick={goToCurrentWeek} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              <TodayIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <IconButton
          onClick={goToNextWeek}
          disabled={isCurrentWeek}
          sx={{ color: isCurrentWeek ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.7)' }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Week Picker Popover */}
      <Popover
        open={Boolean(weekPickerAnchorEl)}
        anchorEl={weekPickerAnchorEl}
        onClose={() => setWeekPickerAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={currentWeekStart}
            onChange={(newDate) => {
              if (newDate) {
                const weekStart = startOfWeek(newDate, { weekStartsOn: 1 });
                const today = new Date();
                const currentWeek = startOfWeek(today, { weekStartsOn: 1 });
                // Don't allow selecting future weeks
                if (weekStart <= currentWeek) {
                  setCurrentWeekStart(weekStart);
                }
              }
              setWeekPickerAnchorEl(null);
            }}
            maxDate={new Date()}
            sx={{
              '& .MuiPickersCalendarHeader-root': {
                color: 'rgba(255, 255, 255, 0.9)'
              },
              '& .MuiPickersCalendarHeader-label': {
                color: 'rgba(255, 255, 255, 0.9)'
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: 'rgba(255, 255, 255, 0.5)'
              },
              '& .MuiPickersDay-root': {
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                '&.Mui-selected': {
                  backgroundColor: '#00897b',
                  '&:hover': {
                    backgroundColor: '#00796b'
                  }
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              },
              '& .MuiPickersDay-today': {
                border: '1px solid #00897b'
              }
            }}
          />
        </LocalizationProvider>
      </Popover>

      {/* Week Calendar View */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        {weekDays.map((day) => (
          <Tooltip
            key={day.date}
            title={
              day.isFuture ? 'Future date' :
              <Box>
                <Typography variant="caption">{format(parseISO(day.date), 'EEEE, MMM d')}</Typography>
                {day.log?.highlight && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>"{day.log.highlight}"</Typography>}
              </Box>
            }
            arrow
          >
            <Box
              onClick={() => !day.isFuture && openEditDialog(day.date)}
              sx={{
                flex: 1,
                minHeight: 80,
                borderRadius: 2,
                backgroundColor: day.isFuture ? 'rgba(255, 255, 255, 0.02)' : getCompletionColor(day.log),
                border: day.isToday ? '2px solid #00897b' : '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 1,
                cursor: day.isFuture ? 'default' : 'pointer',
                opacity: day.isFuture ? 0.4 : 1,
                transition: 'all 0.2s',
                '&:hover': !day.isFuture ? {
                  transform: 'scale(1.02)',
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                } : {}
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.65rem' }}>
                {day.dayName}
              </Typography>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: day.isToday ? 600 : 400 }}>
                {day.dayNum}
              </Typography>
              {day.log?.mood && (
                <Box sx={{ mt: 0.5 }}>
                  {getMoodIcon(day.log.mood, 14)}
                </Box>
              )}
              {/* Small indicators for other metrics */}
              <Box sx={{ display: 'flex', gap: 0.25, mt: 0.5 }}>
                {day.log?.exercised && <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#2196f3' }} />}
                {day.log?.eatenFruits && <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#4caf50' }} />}
                {day.log?.sleptWell && <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#673ab7' }} />}
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Entries for current week */}
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 2 }}>
        Entries this week
      </Typography>
      {weekDays.filter(day => day.log && (day.log.highlight || day.log.mood || day.log.exercised || day.log.eatenFruits || day.log.sleptWell)).reverse().map((day) => (
        <Box
          key={day.date}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 2,
            p: 2,
            mb: 1,
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.7rem' }}>
                {format(parseISO(day.date), 'EEEE, MMM d')}
              </Typography>
              {day.log.highlight && (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5, fontStyle: 'italic' }}>
                  "{day.log.highlight}"
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {day.log.mood && getMoodIcon(day.log.mood, 18)}
              {day.log.exercised && <FitnessCenterIcon sx={{ fontSize: 16, color: '#2196f3' }} />}
              {day.log.eatenFruits && <AppleIcon sx={{ fontSize: 16, color: '#4caf50' }} />}
              {day.log.sleptWell && <BedIcon sx={{ fontSize: 16, color: '#673ab7' }} />}
              <IconButton size="small" onClick={() => openEditDialog(day.date)} sx={{ ml: 1 }}>
                <EditIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.3)' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      ))}

      {weekDays.filter(day => day.log && (day.log.highlight || day.log.mood || day.log.exercised || day.log.eatenFruits || day.log.sleptWell)).length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
            No entries for this week
          </Typography>
        </Box>
      )}

      {/* Mood Selector Popover */}
      <Popover
        open={Boolean(moodAnchorEl)}
        anchorEl={moodAnchorEl}
        onClose={() => setMoodAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            mt: 1,
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            p: 1.5
          }
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          {Object.entries(MOOD_CONFIG).map(([value, config]) => {
            const Icon = config.icon;
            return (
              <IconButton
                key={value}
                onClick={() => handleSetMood(value)}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: todayLog.mood === value ? config.color : 'rgba(255, 255, 255, 0.1)',
                  backgroundColor: todayLog.mood === value ? `${config.color}20` : 'transparent',
                  '&:hover': { borderColor: config.color, transform: 'scale(1.1)' }
                }}
              >
                <Icon sx={{ fontSize: 22, color: todayLog.mood === value ? config.color : 'rgba(255, 255, 255, 0.5)' }} />
              </IconButton>
            );
          })}
        </Box>
      </Popover>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle>
          {editingDate && format(parseISO(editingDate), 'EEEE, MMMM d')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Highlight"
              value={editData.highlight || ''}
              onChange={(e) => setEditData({ ...editData, highlight: e.target.value })}
              margin="dense"
              placeholder="What made this day special?"
            />

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
              How were you feeling?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              {Object.entries(MOOD_CONFIG).map(([value, config]) => {
                const Icon = config.icon;
                return (
                  <IconButton
                    key={value}
                    onClick={() => setEditData({ ...editData, mood: value })}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: editData.mood === value ? config.color : 'rgba(255, 255, 255, 0.1)',
                      backgroundColor: editData.mood === value ? `${config.color}20` : 'transparent'
                    }}
                  >
                    <Icon sx={{ fontSize: 22, color: editData.mood === value ? config.color : 'rgba(255, 255, 255, 0.5)' }} />
                  </IconButton>
                );
              })}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
              Daily habits
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton
                onClick={() => setEditData({ ...editData, exercised: !editData.exercised })}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: editData.exercised ? '#2196f3' : 'rgba(255, 255, 255, 0.1)',
                  backgroundColor: editData.exercised ? 'rgba(33, 150, 243, 0.2)' : 'transparent'
                }}
              >
                <FitnessCenterIcon sx={{ color: editData.exercised ? '#2196f3' : 'rgba(255, 255, 255, 0.3)' }} />
              </IconButton>
              <IconButton
                onClick={() => setEditData({ ...editData, eatenFruits: !editData.eatenFruits })}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: editData.eatenFruits ? '#4caf50' : 'rgba(255, 255, 255, 0.1)',
                  backgroundColor: editData.eatenFruits ? 'rgba(76, 175, 80, 0.2)' : 'transparent'
                }}
              >
                <AppleIcon sx={{ color: editData.eatenFruits ? '#4caf50' : 'rgba(255, 255, 255, 0.3)' }} />
              </IconButton>
              <IconButton
                onClick={() => setEditData({ ...editData, sleptWell: !editData.sleptWell })}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: editData.sleptWell ? '#673ab7' : 'rgba(255, 255, 255, 0.1)',
                  backgroundColor: editData.sleptWell ? 'rgba(103, 58, 183, 0.2)' : 'transparent'
                }}
              >
                <BedIcon sx={{ color: editData.sleptWell ? '#673ab7' : 'rgba(255, 255, 255, 0.3)' }} />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WellnessLog;
